import type { Translator } from '@porotutu/i18n'

export type ListMeta = {
  total: number
  limit: number
  offset: number
  sort: string
  direction: 'asc' | 'desc'
}

export type ListQuery<Sort extends string = string, Status extends string = string> = {
  limit?: number
  offset?: number
  sort?: Sort
  direction?: 'asc' | 'desc'
  status?: Status
  search?: string
} & { [filter: string]: string | number | undefined }

export function sortDirection(column: string, meta: ListMeta): 'asc' | 'desc' | null {
  return meta.sort === column ? meta.direction : null
}

export const PAGE_SIZES = [10, 25, 50, 100] as const

const PAGE_WINDOW = 2

const pageCount = (meta: ListMeta): number =>
  meta.total === 0 ? 1 : Math.floor((meta.total - 1) / meta.limit) + 1

export const currentPage = (meta: ListMeta): number => Math.floor(meta.offset / meta.limit) + 1

export const paginated = (meta: ListMeta): boolean => meta.total > meta.limit

function shownNumbers(meta: ListMeta): number[] {
  const pages = pageCount(meta)
  const current = currentPage(meta)
  const neighbours = Array.from(
    { length: PAGE_WINDOW * 2 + 1 },
    (_, index) => current - PAGE_WINDOW + index,
  )
  const wanted = [1, pages, ...neighbours].filter((number) => number >= 1 && number <= pages)

  return [...new Set(wanted)].sort((one, other) => one - other)
}

export function pageNumbers(meta: ListMeta): Array<number | null> {
  return shownNumbers(meta).reduce<Array<number | null>>((listed, number) => {
    const last = listed[listed.length - 1]

    if (typeof last === 'number' && number - last > 1) listed.push(null)

    listed.push(number)

    return listed
  }, [])
}

export function listSummary(meta: ListMeta, t: Translator): string {
  if (meta.total === 0) return t('pagination.summary_empty')

  return t('pagination.summary', {
    first: meta.offset + 1,
    last: Math.min(meta.offset + meta.limit, meta.total),
    total: meta.total,
  })
}

export function listPosition(meta: ListMeta, t: Translator): string {
  return t('pagination.position', { current: currentPage(meta), pages: pageCount(meta) })
}

type RawQuery = Record<string, unknown>

export function listQueryOf<Sort extends string, Status extends string = never>(
  raw: RawQuery,
  sortable: readonly Sort[],
  statuses: readonly Status[] = [],
  filters: readonly string[] = [],
  enums: Record<string, readonly string[]> = {},
): ListQuery<Sort, Status> {
  const sort = sortable.find((column) => column === raw.sort)
  const direction = raw.direction === 'asc' ? 'asc' : raw.direction === 'desc' ? 'desc' : undefined
  const status = statuses.find((entry) => entry === raw.status)
  const search = String(raw.search ?? '').trim()
  const limit = Number(raw.limit)
  const offset = Number(raw.offset)
  const uuid = (value: unknown) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value ?? ''))
      ? String(value)
      : undefined

  const chosen: Record<string, string | undefined> = {}

  for (const filter of filters) {
    chosen[filter] = uuid(raw[filter])
  }

  for (const [filter, allowed] of Object.entries(enums)) {
    chosen[filter] = allowed.find((entry) => entry === raw[filter])
  }

  return {
    limit: PAGE_SIZES.includes(limit as (typeof PAGE_SIZES)[number]) ? limit : undefined,
    offset: Number.isFinite(offset) && offset > 0 ? offset : undefined,
    sort,
    direction,
    status,
    ...chosen,
    search: search === '' ? undefined : search,
  }
}
